import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { accountsApi } from '@/api'
import { useAuthStore } from '@/store/auth.store'
import { Avatar } from './Avatar'
import { accountPath } from '@/lib/accountPath'
import type { AccountWithStats } from '@olimhub/shared'

interface AccountCardProps {
  account: AccountWithStats
  onFollowChange?: () => void
}

export function AccountCard({ account, onFollowChange }: AccountCardProps) {
  const queryClient = useQueryClient()
  const { isAuthenticated, account: authAccount } = useAuthStore()
  const isOwnProfile = authAccount?.username === account.username

  const [localIsFollowing, setLocalIsFollowing] = useState<boolean | undefined>(undefined)
  const effectiveIsFollowing = localIsFollowing ?? account.isFollowing ?? false

  async function handleFollow() {
    if (!isAuthenticated) return
    const prev = effectiveIsFollowing
    setLocalIsFollowing(!prev)
    try {
      if (prev) {
        await accountsApi.unfollow(account.username)
      } else {
        await accountsApi.follow(account.username)
      }
      void queryClient.invalidateQueries({ queryKey: ['profile', account.username] })
      void queryClient.invalidateQueries({ queryKey: ['profile', authAccount?.username] })
      void queryClient.invalidateQueries({ queryKey: ['followers', account.username] })
      void queryClient.invalidateQueries({ queryKey: ['following', authAccount?.username] })
      void queryClient.invalidateQueries({ queryKey: ['feed'] })
      onFollowChange?.()
    } catch {
      setLocalIsFollowing(prev)
    }
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 16px',
        background: 'var(--surface)',
        borderBottom: '1px solid var(--border)',
      }}
    >
      <Link
        to={accountPath(account)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          flex: 1,
          minWidth: 0,
          textDecoration: 'none',
        }}
      >
        <Avatar
          name={account.displayName || account.username}
          avatarUrl={account.avatarUrl}
          animalAvatar={account.animalAvatar}
          accountType={account.accountType}
          size="md"
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontFamily: 'var(--font-sans)',
              fontWeight: 700,
              color: 'var(--text-strong)',
              fontSize: 'var(--fs-body)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {account.displayName || account.username}
          </div>
          <div
            style={{
              fontFamily: 'var(--font-sans)',
              color: 'var(--text-muted)',
              fontSize: 'var(--fs-sm)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            @{account.username}
          </div>
        </div>
      </Link>

      {isAuthenticated && !isOwnProfile && !effectiveIsFollowing && (
        <button
          type="button"
          onClick={handleFollow}
          style={{
            padding: '7px 16px',
            borderRadius: 'var(--radius-pill)',
            border: 'none',
            background: 'var(--brand)',
            color: 'var(--on-brand)',
            fontFamily: 'var(--font-sans)',
            fontWeight: 600,
            fontSize: 'var(--fs-sm)',
            cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          Follow
        </button>
      )}
    </div>
  )
}
